// src/services/departmentService.js
// REPLACE YOUR ENTIRE departmentService.js WITH THIS VERSION

import { supabase } from './supabase';

export const departmentService = {
  async getDepartments(company_id) {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('company_id', company_id)
      .order('name');

    if (error) throw error;
    return data;
  },

  async addDepartment(department, company_id) {
    const { data, error } = await supabase
      .from('departments')
      .insert([{ 
        ...department, 
        company_id,
        // Default leave settings - ONLY sick and local
        sick_leave_days: 7,
        local_leave_days: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateDepartment(id, department, company_id) {
    const { data, error } = await supabase
      .from('departments')
      .update({
        ...department,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('company_id', company_id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateDepartmentLeaveSettings(id, leaveSettings, company_id) {
    const { data, error } = await supabase
      .from('departments')
      .update({
        sick_leave_days: leaveSettings.sick_leave_days,
        local_leave_days: leaveSettings.local_leave_days,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('company_id', company_id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getDepartmentLeaveSettings(department_id, company_id) {
    const { data, error } = await supabase
      .from('departments')
      .select('sick_leave_days, local_leave_days')
      .eq('id', department_id)
      .eq('company_id', company_id)
      .single();

    if (error) throw error;
    
    // Return default values if not set
    return {
      sick_leave_days: data?.sick_leave_days || 7,
      local_leave_days: data?.local_leave_days || 0
    };
  },

  async deleteDepartment(id, company_id) {
    // First check if there are employees in this department
    const { data: employees, error: employeeError } = await supabase
      .from('employees')
      .select('id')
      .eq('department', (
        // Get department name first
        await supabase
          .from('departments')
          .select('name')
          .eq('id', id)
          .eq('company_id', company_id)
          .single()
      ).data?.name)
      .eq('company_id', company_id);

    if (employeeError && employeeError.code !== 'PGRST116') throw employeeError;

    if (employees && employees.length > 0) {
      throw new Error('Cannot delete department with existing employees. Please reassign employees first.');
    }

    const { error } = await supabase
      .from('departments')
      .delete()
      .eq('id', id)
      .eq('company_id', company_id);

    if (error) throw error;
    return true;
  },

  // Function to get department by name (for employee management)
  async getDepartmentByName(name, company_id) {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('name', name)
      .eq('company_id', company_id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Function to get leave usage for an employee in a department
  async getEmployeeLeaveUsage(employee_id, department_id, company_id, year = new Date().getFullYear()) {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;

    const { data, error } = await supabase
      .from('leaves')
      .select('type, start_date, end_date, leave_duration')
      .eq('employee_id', employee_id)
      .eq('company_id', company_id)
      .eq('status', 'approved')
      .gte('start_date', startDate)
      .lte('end_date', endDate);

    if (error) throw error;

    // Calculate total leave days by type - ONLY sick and local
    const leaveUsage = {
      sick_leave_used: 0,
      local_leave_used: 0,
      unpaid_leave_used: 0,
      other_leave_used: 0
    };

    if (data) {
      data.forEach(leave => {
        // Calculate days for this leave record
        const startDate = new Date(leave.start_date);
        const endDate = new Date(leave.end_date);
        const daysDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        
        let leaveDays = daysDiff;
        
        // Adjust for half-day leaves
        if (leave.leave_duration === 'half_day') {
          if (daysDiff === 1) {
            leaveDays = 0.5; // Single half-day
          } else {
            leaveDays = daysDiff - 1 + 0.5; // Multi-day with first/last as half-days
          }
        }

        // Add to appropriate leave type - ONLY sick and local
        switch (leave.type) {
          case 'sick':
            leaveUsage.sick_leave_used += leaveDays;
            break;
          case 'local':
            leaveUsage.local_leave_used += leaveDays;
            break;
          case 'unpaid':
            leaveUsage.unpaid_leave_used += leaveDays;
            break;
          case 'other':
            leaveUsage.other_leave_used += leaveDays;
            break;
        }
      });
    }

    return leaveUsage;
  }
};